import api from '../lib/axios';
import type { ApiResponse } from '@maios/shared';

// --- Types ---

export interface TemplateItem {
  templateId: string;
  name: string;
  contentPreview: string;
  sortOrder: number;
}

export interface TemplateCategory {
  categoryId: string;
  name: string;
  sortOrder: number;
  templates: TemplateItem[];
}

export interface TemplateListResponse {
  categories: TemplateCategory[];
}

export interface TemplateDetail {
  templateId: string;
  projectId: string;
  categoryId: string;
  name: string;
  content: string;
  sortOrder: number;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  category: { categoryId: string; name: string };
}

// --- API ---

export async function fetchTemplates(projectId: string) {
  const res = await api.get<ApiResponse<TemplateListResponse>>(
    `/projects/${projectId}/issue-templates`,
  );
  return res.data.data!;
}

export async function fetchTemplate(projectId: string, templateId: string) {
  const res = await api.get<ApiResponse<TemplateDetail>>(
    `/projects/${projectId}/issue-templates/${templateId}`,
  );
  return res.data.data!;
}

// --- Categories API ---

export interface CategoryItem {
  categoryId: string;
  name: string;
  sortOrder: number;
  templateCount: number;
}

export async function fetchCategories(projectId: string) {
  const res = await api.get<ApiResponse<CategoryItem[]>>(
    `/projects/${projectId}/issue-templates/categories`,
  );
  return res.data.data!;
}

export async function createCategory(projectId: string, data: { name: string; sortOrder?: number }) {
  const res = await api.post<ApiResponse<CategoryItem>>(
    `/projects/${projectId}/issue-templates/categories`,
    data,
  );
  return res.data.data!;
}

export async function updateCategory(projectId: string, categoryId: string, data: { name?: string; sortOrder?: number }) {
  const res = await api.put<ApiResponse<CategoryItem>>(
    `/projects/${projectId}/issue-templates/categories/${categoryId}`,
    data,
  );
  return res.data.data!;
}

export async function deleteCategory(projectId: string, categoryId: string) {
  await api.delete(`/projects/${projectId}/issue-templates/categories/${categoryId}`);
}

// --- Templates CRUD API ---

export async function createTemplate(projectId: string, data: { categoryId: string; name: string; content: string; sortOrder?: number }) {
  const res = await api.post<ApiResponse<TemplateDetail>>(
    `/projects/${projectId}/issue-templates`,
    data,
  );
  return res.data.data!;
}

export async function updateTemplate(projectId: string, templateId: string, data: { categoryId?: string; name?: string; content?: string; sortOrder?: number }) {
  const res = await api.put<ApiResponse<TemplateDetail>>(
    `/projects/${projectId}/issue-templates/${templateId}`,
    data,
  );
  return res.data.data!;
}

export async function deleteTemplate(projectId: string, templateId: string) {
  await api.delete(`/projects/${projectId}/issue-templates/${templateId}`);
}

// --- Seed defaults ---

export async function seedDefaults(projectId: string) {
  const res = await api.post<ApiResponse<void>>(
    `/projects/${projectId}/issue-templates/seed-defaults`,
  );
  return res.data;
}
